/**
 * Blossom (BUD-01/02/03) — storing a blob on a server the user chose, addressed by
 * its own hash.
 *
 * Blossom is not NIP-96 with different spelling. The difference that shapes this
 * module is that a blob's *name is its sha256*: the client hashes the bytes before
 * it asks for permission to store them, the authorization event names that hash, and
 * the URL the server hands back has the hash in it. That makes the whole exchange
 * checkable, and this module's job is to actually check it rather than to trust a
 * server that just accepted a file.
 *
 * What that means in practice, and why each part is here:
 *
 *  - **Authorization is kind 24242, not NIP-98's 27235.** Blossom defines its own
 *    kind with `t`/`x`/`expiration` tags and no `u`/`method`, so a Blossom auth event
 *    is *not* pinned to one URL. Signing one is handing out a bearer token to upload
 *    under the user's identity, which is why `buildBlossomAuth` caps `expiration`
 *    hard (`MAX_BLOSSOM_AUTH_TTL_SECONDS`) instead of taking whatever a caller asks
 *    for. A 24-hour 24242 in a server's log is a 24-hour licence.
 *  - **A server list is relay-supplied data.** Kind 10063 is a public replaceable
 *    event; anything can put anything in it, including a URL aimed at the reader's
 *    own network. Every entry goes through `assertSafeBlossomUrl` before a request is
 *    made to it, and `parseBlossomServerList` drops the ones that fail rather than
 *    throwing, so one hostile row cannot blank a usable list.
 *  - **The response is untrusted too.** The `url` in a blob descriptor ends up in a
 *    note the user signs and other clients render. It is checked against the same
 *    host rules *and* required to address the hash we authorized — see
 *    `parseBlobDescriptor`. A server that returns a different `sha256` stored
 *    something other than what the user handed it, and publishing that URL would sign
 *    a claim about bytes the user never saw.
 *
 * The host rules below are deliberately a copy of the shape used elsewhere in the
 * codebase rather than an import of it: this is the bottom layer, the CLI needs the
 * same answers with no app around it, and the rules are about what *this* module will
 * send a signature to. Widening them is the change to argue about.
 */

import { isHex32 } from "./hex";
import type { HasTags } from "./tags";
import type { EventTemplate, NostrEvent } from "./types";

/** Kind 24242 — the Blossom authorization event (BUD-01). */
export const BLOSSOM_AUTH_KIND = 24242;

/**
 * The longest life a kind-24242 may be given, in seconds.
 *
 * Short because the event is a bearer token: whoever holds it can store blobs
 * under the user's pubkey at any Blossom server until it expires, and the server we
 * hand it to is the party most able to abuse it. A minute is enough for an upload
 * that is already in flight and useless as a stolen credential.
 */
export const MAX_BLOSSOM_AUTH_TTL_SECONDS = 60;

/**
 * How many servers from a kind-10063 we will actually try.
 *
 * A list is an unbounded array of strings written by anyone who can publish under
 * the user's key — including a client with a bug. Without a cap, "try the next
 * server" is a loop that uploads the same file hundreds of times from a phone.
 */
export const MAX_BLOSSOM_SERVERS = 8;

/** Why a Blossom operation was refused. */
export type BlossomErrorCode =
  | "bad-url"
  | "insecure"
  | "credentials-in-url"
  | "blocked-host"
  | "bad-port"
  | "bad-path"
  | "bad-hash"
  | "bad-response"
  | "hash-mismatch"
  | "size-mismatch"
  | "no-servers";

export class BlossomError extends Error {
  readonly code: BlossomErrorCode;
  constructor(code: BlossomErrorCode, message: string) {
    super(message);
    this.name = "BlossomError";
    this.code = code;
  }
}

/**
 * A registrable domain: dot-separated labels, at least two of them.
 *
 * Two labels minimum is what refuses `localhost`, `router` and every other bare
 * name that only resolves inside the network the client is running in.
 */
const DOMAIN =
  /^(?=.{1,253}$)(?!-)[a-z0-9-]{1,63}(?<!-)(?:\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/;

/**
 * Last labels that never belong to a public host.
 *
 * Structure alone cannot decide this. `metadata.google.internal` has dots, letters
 * and a non-numeric final label, and inside a cloud VM it resolves to the instance
 * metadata service — the single highest-value target for a request made on someone
 * else's behalf. RFC 6761 and RFC 8375 reserve most of these; `.internal` is the
 * convention every large cloud uses for private zones.
 */
const RESERVED_TLDS = new Set([
  "internal",
  "local",
  "localhost",
  "localdomain",
  "home",
  "arpa",
  "test",
  "example",
  "invalid",
  "onion",
  "alt",
  "lan",
  "intranet",
  "private",
  "corp",
]);

/**
 * The URL of a Blossom server we are willing to talk to, or a throw explaining why
 * not.
 *
 * `https` only, no credentials, no port, a publicly registrable host, and no path
 * beyond `/`. Each rule closes a specific hole:
 *
 *  - a port turns a list entry into a way to sweep services the client's machine can
 *    reach that a public web server never answers on;
 *  - credentials in the URL are sent to whoever the host resolves to;
 *  - BUD-01 puts every endpoint at the root, so an entry carrying a path is
 *    ambiguous — `/upload` relative to it is a *different* endpoint than the one the
 *    operator published, and guessing wrong means PUTting a file and a signature
 *    somewhere nobody chose.
 */
export function assertSafeBlossomUrl(raw: string): URL {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    throw new BlossomError("bad-url", "That is not a valid server address.");
  }
  if (url.protocol !== "https:") {
    throw new BlossomError("insecure", "Blossom servers must use HTTPS.");
  }
  if (url.username !== "" || url.password !== "") {
    throw new BlossomError(
      "credentials-in-url",
      "That server address embeds credentials, which Setu will not send.",
    );
  }
  if (url.port !== "") {
    throw new BlossomError(
      "bad-port",
      "That server address names a port, which Setu will not use.",
    );
  }
  const host = url.hostname.toLowerCase();
  if (!DOMAIN.test(host)) {
    throw new BlossomError(
      "blocked-host",
      "That server address is not a public host name.",
    );
  }
  const labels = host.split(".");
  const tld = labels[labels.length - 1] as string;
  // An all-numeric last label is a dotted IP address wearing a domain shape:
  // `127.0.0.1` satisfies every label rule above and must still be refused.
  if (/^\d+$/.test(tld) || RESERVED_TLDS.has(tld)) {
    throw new BlossomError(
      "blocked-host",
      "That server address is not publicly routable.",
    );
  }
  if (url.pathname !== "/" || url.search !== "" || url.hash !== "") {
    throw new BlossomError(
      "bad-path",
      "A Blossom server is named by its origin alone — drop the path.",
    );
  }
  return url;
}

/** True when this string is a server address `assertSafeBlossomUrl` accepts. */
export function isSafeBlossomServer(raw: string): boolean {
  try {
    assertSafeBlossomUrl(raw);
    return true;
  } catch {
    return false;
  }
}

/**
 * A server address reduced to the one form we compare and store: its origin.
 *
 * Returns `undefined` rather than throwing, because the callers that need this are
 * dealing with lists where one bad row must not discard the rest.
 */
export function normalizeBlossomServer(raw: string): string | undefined {
  try {
    return assertSafeBlossomUrl(raw).origin;
  } catch {
    return undefined;
  }
}

/**
 * The raw `server` values on a kind-10063, in list order.
 *
 * Unvalidated on purpose. This is what an *editor* seeds from: dropping the entries
 * this build refuses to upload to would mean the next save deletes them, and a
 * server another client published is not ours to remove because we would not use it.
 * The upload path uses `parseBlossomServerList` instead.
 */
export function blossomServerTags(
  event: HasTags | undefined,
): readonly string[] {
  if (!event) return [];
  const out: string[] = [];
  for (const tag of event.tags) {
    if (tag[0] !== "server") continue;
    const value = tag[1]?.trim();
    if (value === undefined || value === "") continue;
    out.push(value);
  }
  return out;
}

/**
 * The servers from a kind-10063 that we will actually upload to: validated,
 * normalized to origins, deduplicated, in the order the author listed them.
 *
 * Order is preserved because BUD-03 makes it meaningful — the first entry is the
 * user's stated preference, and reordering it would silently move their files to a
 * different operator.
 */
export function parseBlossomServerList(
  event: HasTags | undefined,
): readonly string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of blossomServerTags(event)) {
    if (out.length >= MAX_BLOSSOM_SERVERS) break;
    const origin = normalizeBlossomServer(raw);
    if (origin === undefined || seen.has(origin)) continue;
    seen.add(origin);
    out.push(origin);
  }
  return out;
}

/** Where BUD-01 puts the upload endpoint, for a server we have validated. */
export function blossomUploadUrl(server: string): string {
  return `${assertSafeBlossomUrl(server).origin}/upload`;
}

export interface BlossomAuthInput {
  /** Lowercase hex sha256 of the exact bytes about to be sent. */
  readonly sha256: string;
  /** Shown to the reader by their signer, so it says what is being signed. */
  readonly reason?: string;
  /** Requested lifetime; clamped to `MAX_BLOSSOM_AUTH_TTL_SECONDS`. */
  readonly ttlSeconds?: number;
  /** Unix seconds. Injected by tests; defaults to now. */
  readonly now?: number;
}

/**
 * The kind-24242 that authorizes storing one specific blob. Caller signs it.
 *
 * `x` is what limits the damage: the event authorizes *these bytes* and no others,
 * so a server that keeps the signature cannot use it to store something else under
 * the user's identity. The `expiration` cap limits how long it can reuse it for the
 * same blob at other servers, which is the residual we accept — see
 * `MAX_BLOSSOM_AUTH_TTL_SECONDS`.
 */
export function buildBlossomAuth(input: BlossomAuthInput): EventTemplate {
  const { sha256, reason, ttlSeconds, now } = input;
  if (!isHex32(sha256)) {
    throw new BlossomError(
      "bad-hash",
      "A Blossom upload must be authorized for a sha256 hash.",
    );
  }
  const created = Math.floor(now ?? Date.now() / 1000);
  // Clamped both ways. A caller asking for a day gets a minute; a caller asking for
  // zero or a negative window gets an event that is already expired, which every
  // server correctly refuses and which reads as a bug rather than as no expiry.
  const ttl = Math.min(
    Math.max(Math.floor(ttlSeconds ?? MAX_BLOSSOM_AUTH_TTL_SECONDS), 1),
    MAX_BLOSSOM_AUTH_TTL_SECONDS,
  );
  return {
    kind: BLOSSOM_AUTH_KIND,
    content: reason ?? "Upload a file",
    tags: [
      ["t", "upload"],
      ["x", sha256],
      ["expiration", String(created + ttl)],
    ],
    created_at: created,
  };
}

/** `Authorization: Nostr <base64 event>`, per BUD-01. */
export function blossomAuthHeader(event: NostrEvent): string {
  const bytes = new TextEncoder().encode(JSON.stringify(event));
  // `btoa` throws on anything outside Latin-1, and `content` is a free-text string a
  // caller chose — an emoji in it would otherwise turn a working upload into an
  // exception at the moment the request is built.
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `Nostr ${btoa(binary)}`;
}

/** A stored blob, as BUD-02 describes it. */
export interface BlobDescriptor {
  readonly url: string;
  readonly sha256: string;
  readonly size: number;
  /** MIME type as the server recorded it, when it looks like one. */
  readonly type?: string;
}

/** `type/subtype`, no parameters. Anything else is dropped, never carried. */
const MIME = /^[a-z0-9][a-z0-9.+-]*\/[a-z0-9][a-z0-9.+*-]*$/i;

/**
 * The URL must address the blob we authorized.
 *
 * BUD-02 serves a blob at `/<sha256>` with an optional extension, and holding the
 * server to that is what makes its answer verifiable by anyone who later reads the
 * note: the hash is in the URL, so a reader can fetch it and check. Without this
 * check a server could return any public https URL it liked — its own hosting of an
 * unrelated file, or a third party's — and the user would sign a note pointing at it.
 */
function assertAddressesBlob(url: URL, sha256: string): void {
  const last = url.pathname.split("/").pop() ?? "";
  if (!last.toLowerCase().startsWith(sha256)) {
    throw new BlossomError(
      "bad-response",
      "The server returned a URL that does not address the file that was uploaded.",
    );
  }
}

/**
 * A media URL we are willing to publish.
 *
 * Same host rules as a server address, minus the origin-only requirement — the blob
 * lives at a path, and plenty of servers serve it from a CDN host of their own.
 */
function assertSafeBlobUrl(raw: string): URL {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw new BlossomError(
      "bad-response",
      "The server returned something that is not a URL.",
    );
  }
  // Credentials are checked here and not through the origin below: `URL.origin`
  // discards `user:pw@`, so validating the origin alone would wave through a
  // descriptor URL carrying credentials the reader's browser would then send.
  if (url.username !== "" || url.password !== "") {
    throw new BlossomError(
      "credentials-in-url",
      "The server returned a URL with credentials in it.",
    );
  }
  // Everything else goes through the origin validator, so scheme, port and host
  // rules cannot drift apart from the ones applied to the server itself.
  assertSafeBlossomUrl(url.origin);
  return url;
}

export interface BlobExpectation {
  /** The hash we computed locally and authorized. */
  readonly sha256: string;
  /** Byte length we sent, when the caller knows it. */
  readonly size?: number;
}

/**
 * Read a blob descriptor, refusing anything we cannot publish honestly.
 *
 * The hash comparison is the point of the whole exercise. `sha256` coming back
 * different means the stored blob is not the file the user chose — a re-encode, a
 * truncation, or a substitution — and the URL for it would be a signed claim about
 * bytes nobody has seen. Same for `size`: it is published information, and a server
 * that reports a length it did not store is not a server whose descriptor we relay.
 */
export function parseBlobDescriptor(
  body: unknown,
  expected: BlobExpectation,
): BlobDescriptor {
  if (typeof body !== "object" || body === null) {
    throw new BlossomError(
      "bad-response",
      "The server returned no descriptor.",
    );
  }
  const payload = body as {
    url?: unknown;
    sha256?: unknown;
    size?: unknown;
    type?: unknown;
  };

  if (typeof payload.sha256 !== "string" || !isHex32(payload.sha256)) {
    throw new BlossomError(
      "bad-response",
      "The server's descriptor carried no usable sha256.",
    );
  }
  if (payload.sha256 !== expected.sha256) {
    throw new BlossomError(
      "hash-mismatch",
      "The server stored something other than the file that was sent, so Setu will not publish a link to it.",
    );
  }

  if (
    typeof payload.size !== "number" ||
    !Number.isInteger(payload.size) ||
    payload.size < 0
  ) {
    throw new BlossomError(
      "bad-response",
      "The server's descriptor carried no usable size.",
    );
  }
  if (expected.size !== undefined && payload.size !== expected.size) {
    throw new BlossomError(
      "size-mismatch",
      "The server reported a different file size than was sent.",
    );
  }

  if (typeof payload.url !== "string" || payload.url === "") {
    throw new BlossomError(
      "bad-response",
      "The server's descriptor carried no URL.",
    );
  }
  const url = assertSafeBlobUrl(payload.url);
  assertAddressesBlob(url, expected.sha256);

  const type =
    typeof payload.type === "string" && MIME.test(payload.type)
      ? payload.type
      : undefined;

  return {
    url: url.toString(),
    sha256: payload.sha256,
    size: payload.size,
    ...(type ? { type } : {}),
  };
}
